import {
  Component,
  computed,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  BasketService,
  SinglePlanPromoPriceComponent,
  TitlePriceVariant,
} from '@basket';
import { DocumentPathService, StickyDirective } from '@common';
import {
  AccordionComponent,
  AccordionPanelComponent,
  GridColumnComponent,
  GridRowComponent,
  IconComponent,
  LinkComponent,
} from '@components';
import { CustomerService } from '@customer';
import { BasketComponent } from '@data-access';
import { CheckoutService } from '@features/checkout';
import { MultiPlanService } from '@features/multi-plan';
import {
  CheckboxComponent,
  FormDisabledDirective,
  FormFieldComponent,
  FormFieldDirective,
  FormSubmitButtonComponent,
  FormSubmitDirective,
  SelectComponent,
  SortcodeSingleInputComponent,
  TextInputComponent,
} from '@forms';
import {
  CurrencyFormatPipe,
  provideTranslationDictionaryMapping,
  TranslatePipe,
} from '@i18n';
import { QMSanitiseDirective } from '@qm';
import {
  accountHolderConfirmationFormField,
  accountHolderFormField,
  accountNumberFormField,
  paperlessFormField,
  paymentDateFormField,
  paymentDateOptions,
  sortcodeFormField,
  termsAndConditionsFormField,
} from '../../constants/checkout-form-fields.constant';
import { translation } from '../../i18n/translation.constant';
import { DirectDebitFormValue } from '../../models/direct-debit/direct-debit-form-value.type';
import { DirectDebitForm } from '../../models/direct-debit/direct-debit-form.interface';
import { DirectDebitFormBuilder } from '../../services/direct-debit-form-builder/direct-debit-form-builder.service';

@Component({
  selector: 'app-direct-debit-form',
  templateUrl: './direct-debit-form.component.html',
  styleUrl: './direct-debit-form.component.scss',
  standalone: true,
  providers: [
    DirectDebitFormBuilder,
    provideTranslationDictionaryMapping(translation),
  ],
  imports: [
    AccordionComponent,
    AccordionPanelComponent,
    BasketComponent,
    CheckboxComponent,
    FormDisabledDirective,
    FormFieldComponent,
    FormFieldDirective,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    IconComponent,
    LinkComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    SelectComponent,
    TextInputComponent,
    TranslatePipe,
    SortcodeSingleInputComponent,
    GridRowComponent,
    GridColumnComponent,
    SinglePlanPromoPriceComponent,
    CurrencyFormatPipe,
    StickyDirective,
  ],
})
export class DirectDebitFormComponent implements OnInit {
  private readonly customerService = inject(CustomerService);
  readonly formBuilderService = inject(DirectDebitFormBuilder);
  private readonly documentPathService = inject(DocumentPathService);
  readonly checkoutService = inject(CheckoutService);
  readonly basketService = inject(BasketService);
  readonly multiPlanService = inject(MultiPlanService);
  readonly customerHasSelectedToPurchasePlan =
    this.customerService.customerSelectedToPurchasePlan;
  readonly termsUrl = this.documentPathService.termsAndConditionsUrl;
  readonly ipidUrl = this.documentPathService.ipidUrl;
  @Output() submitted = new EventEmitter<DirectDebitFormValue>();
  form?: FormGroup<DirectDebitForm>;
  formSubmitted = false;
  validationAttempted = false;

  readonly isMultiPlanJourney = this.basketService.isMultiPlanJourney();
  readonly inWarranty =
    this.basketService.basketItemApplianceDetails().inWarranty;

  readonly formFields = {
    accountHolder: accountHolderFormField,
    accountHolderConfirmation: accountHolderConfirmationFormField,
    accountNumber: accountNumberFormField,
    paperless: paperlessFormField,
    paymentDate: paymentDateFormField,
    paymentDateOptions,
    sortcode: sortcodeFormField,
    termsAndConditions: termsAndConditionsFormField,
  };

  readonly total = computed(() =>
    this.basketService._showMultiPlanJourney()
      ? this.multiPlanService.total()
      : this.basketService
          .basketItemQuoteDetailsPaymentOption()
          .subsequentPayment.toFixed(2),
  );
  protected readonly TitlePriceVariant = TitlePriceVariant;

  readonly hasPromo = () => this.basketService.basketItemQuoteHasPromo();

  ngOnInit(): void {
    this.form = this.formBuilderService.build();
  }

  public resetSubmissionState(): void {
    this.formSubmitted = false;
  }

  public setValidationAttempted(): void {
    this.validationAttempted = true;
  }

  submitForm(formValue: DirectDebitFormValue): void {
    this.formSubmitted = true;
    this.submitted.emit(formValue);
  }
}
